import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageCircleOff } from 'lucide-react';
import { api } from '@/lib/api';

function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return 'ended';
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (d > 0) return `${d}d ${pad(h)}h`;
  if (h > 0) return `${pad(h)}h ${pad(m)}m`;
  return `${pad(m)}m`;
}

/**
 * Small mute icon shown next to a banned player. Shares the
 * `admin-chat-bans` query cache so it costs nothing to render per row.
 * Silently renders nothing when the user isn't allowed to hit the admin
 * endpoint (query errors are ignored).
 */
export default function ChatBanMuteIcon({ playerId }: { playerId: number }) {
  const q = useQuery({
    queryKey: ['admin-chat-bans'],
    queryFn: api.listAdminChatBans,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const ban = (q.data?.bans ?? []).find(
    (b) =>
      b.player.id === playerId &&
      b.active &&
      Date.parse(b.endsAtUtc) > now,
  );
  if (!ban) return null;
  const delta = Date.parse(ban.endsAtUtc) - now;
  return (
    <span
      className="inline-flex items-center leading-none text-rose-300"
      title={`Chat ban · ends in ${formatCountdown(delta)}`}
      aria-label="Chat banned"
    >
      <MessageCircleOff size={14} strokeWidth={2} />
    </span>
  );
}
